import { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { GSIcon, GSIconName } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import { TitledTooltip } from 'src/components/shared/TitledTooltip'
import { ContactChannel } from './types'

interface Props {
  lastContact: { channel: ContactChannel; daysAgo: number } | null
}

const CHANNEL_ICON: Record<ContactChannel, GSIconName> = {
  call: 'gs_call',
  text: 'gs_bubble_annotation_4_2',
  note: 'gs_notepad_2',
}

/** Lives here rather than in `ContactHistoryItem` — that file already imports from this one. */
export const contactChannelTitle = {
  call: 'Called driver',
  text: 'Texted driver',
  note: 'Note added',
}

/** "2 hours ago" / "3 days ago" / "2 weeks ago" — coarse on purpose, the exact minute is noise. */
export const formatContactAge = (daysAgo: number): string => {
  if (daysAgo <= 0) {
    return 'Today'
  }

  if (daysAgo === 1) {
    return '1 day ago'
  }

  if (daysAgo < 7) {
    return `${daysAgo} days ago`
  }

  const weeks = Math.floor(daysAgo / 7)

  return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
}

const LastContactCellComponent = ({ lastContact }: Props) => {
  if (!lastContact) {
    return <span>-</span>
  }

  const iconName = CHANNEL_ICON[lastContact.channel] || 'gs_notepad_2'
  const age = formatContactAge(lastContact.daysAgo)

  return (
    <div css={tw`flex flex-row items-center gap-2`}>
      <GSIcon iconName={iconName} width={20} height={20} color={colors.GRAY_BLACK} />
      {/* Same affordance as the `Details` link next to a deactivated contractor's status chip:
          underlined black `subtitle1` at weight 500, opening a `TitledTooltip` on hover. The grid
          row only carries channel + timestamp — who logged it and their note only show in the
          drawer, which fetches the full contact from the server. */}
      <TitledTooltip
        title="Last external contact"
        content={
          <Typography variant="body2" color={colors.SHADES_GREY_300}>
            {`${contactChannelTitle[lastContact.channel]} ${age.toLowerCase()}`}
          </Typography>
        }
      >
        <Typography fontWeight={500} variant="subtitle1" color="black" css={tw`underline`}>
          {age}
        </Typography>
      </TitledTooltip>
    </div>
  )
}

export const LastContactCell = memo(LastContactCellComponent)
